import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import { useLearningProgress } from '../hooks/useLearningProgress'
import { useVirtualPortfolio } from '../hooks/useVirtualPortfolio'
import { useTransactions } from '../hooks/useTransactions'
import Navigation from '../components/Navigation'
import EnhancedLoadingSpinner from '../components/EnhancedLoadingSpinner'
import { 
  User as UserIcon, 
  Settings, 
  Mail, 
  Edit3, 
  Save, 
  X, 
  DollarSign, 
  BookOpen, 
  Trophy, 
  TrendingUp,
  LogOut,
  Calendar,
  Target,
  BarChart3,
  Shield,
  Star
} from 'lucide-react'
import { supabase } from '../lib/supabase'

const ProfilePage: React.FC = () => {
  const { user, loading: authLoading, signOut } = useAuth()
  const { progress: learningProgress, achievements, loading: learningLoading } = useLearningProgress(user?.id)
  const { totalValue, totalGainLoss, totalGainLossPercent, loading: portfolioLoading } = useVirtualPortfolio()
  const { getSummaryStats, loading: transactionsLoading } = useTransactions()
  
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingAge, setIsEditingAge] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [editedAge, setEditedAge] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  // Initialize edit values when user data loads
  useEffect(() => {
    if (user) {
      setEditedName(user.name)
      setEditedAge(user.age)
    }
  }, [user])

  const handleSaveName = async () => {
    if (!user || !editedName.trim()) return
    
    setIsSaving(true)
    try {
      const { error } = await supabase
        .from('users')
        .update({ name: editedName.trim() })
        .eq('id', user.id)
      
      if (error) throw error
      setIsEditingName(false)
    } catch (error) {
      console.error('Error updating name:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAge = async () => {
    if (!user || editedAge < 13 || editedAge > 18) return
    
    setIsSaving(true)
    try {
      const { error } = await supabase
        .from('users')
        .update({ age: editedAge })
        .eq('id', user.id)
      
      if (error) throw error
      setIsEditingAge(false)
    } catch (error) {
      console.error('Error updating age:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      window.location.href = '/'
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  // Loading state
  if (authLoading || learningLoading || portfolioLoading || transactionsLoading) {
    return (
      <EnhancedLoadingSpinner
        title="Loading Your Profile"
        subtitle="Gathering your personal information and achievements..."
        icons={[
          <UserIcon className="w-8 h-8 text-primary-500" />,
          <Settings className="w-8 h-8 text-primary-500" />,
          <Trophy className="w-8 h-8 text-primary-500" />,
          <DollarSign className="w-8 h-8 text-primary-500" />
        ]}
        showCircularProgress={true}
      />
    )
  }

  // Not authenticated - show guest view
  if (!user) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="container mx-auto px-6 pt-24 pb-10 max-w-4xl">
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-8">
              <UserIcon className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-4xl font-bold text-background-100 mb-4">
              Create Your Profile
            </h1>
            
            <p className="text-xl text-background-300 mb-8 max-w-2xl mx-auto">
              Sign in to track your financial progress, save your budgets, and access personalized insights.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="luxury-card text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-background-100 mb-2">
                  Track Progress
                </h3>
                <p className="text-background-300 text-sm">
                  Monitor your financial growth and achievements over time
                </p>
              </div>
              
              <div className="luxury-card text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-background-100 mb-2">
                  Save Data
                </h3>
                <p className="text-background-300 text-sm">
                  Keep your budgets, transactions, and portfolio safe
                </p>
              </div>
              
              <div className="luxury-card text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-background-100 mb-2">
                  Personalized Insights
                </h3>
                <p className="text-background-300 text-sm">
                  Get AI-powered recommendations tailored to your goals
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = '/auth?mode=signup'}
                className="luxury-button primary text-lg px-8 py-4"
              >
                Create Account
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = '/auth?mode=login'}
                className="luxury-button secondary text-lg px-8 py-4"
              >
                Sign In
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const stats = getSummaryStats()
  const monthlySpending = stats.totalExpenses

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Left Sidebar - User Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-background-900 border border-background-700 rounded-2xl p-6"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserIcon className="w-10 h-10 text-primary-400" />
                </div>
                <h2 className="text-xl font-bold text-background-100">Profile Information</h2>
              </div>

              {/* Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-background-300 mb-2">
                  Name
                </label>
                {isEditingName ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="flex-1 px-3 py-2 bg-background-800 border border-background-600 rounded-lg text-background-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your name"
                    />
                    <button
                      onClick={handleSaveName}
                      disabled={isSaving || !editedName.trim()}
                      className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false)
                        setEditedName(user.name)
                      }}
                      className="p-2 bg-background-700 hover:bg-background-600 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-background-300" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-background-100">{user.name}</span>
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="p-1 hover:bg-background-700 rounded transition-colors"
                    >
                      <Edit3 className="w-4 h-4 text-background-400" />
                    </button>
                  </div>
                )}
              </div>

              {/* Age Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-background-300 mb-2">
                  Age
                </label>
                {isEditingAge ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={editedAge}
                      onChange={(e) => setEditedAge(parseInt(e.target.value) || 0)}
                      min="13"
                      max="18"
                      className="flex-1 px-3 py-2 bg-background-800 border border-background-600 rounded-lg text-background-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your age"
                    />
                    <button
                      onClick={handleSaveAge}
                      disabled={isSaving || editedAge < 13 || editedAge > 18}
                      className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingAge(false)
                        setEditedAge(user.age)
                      }}
                      className="p-2 bg-background-700 hover:bg-background-600 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-background-300" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-background-100">{user.age} years old</span>
                    <button
                      onClick={() => setIsEditingAge(true)}
                      className="p-1 hover:bg-background-700 rounded transition-colors"
                    >
                      <Edit3 className="w-4 h-4 text-background-400" />
                    </button>
                  </div>
                )}
              </div>

              {/* Email (Read-only) */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-background-300 mb-2">
                  Email
                </label>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-background-400" />
                  <span className="text-background-100">{user.email}</span>
                </div>
              </div>

              {/* Learning Level */}
              {learningProgress && (
                <div className="border-t border-background-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-background-300">Learning Level</span>
                    <span className="text-background-100 font-bold">Level {learningProgress.level}</span>
                  </div>
                  <div className="w-full bg-background-800 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(learningProgress.total_xp_earned % 1000) / 10}%` }}
                    />
                  </div>
                  <div className="text-xs text-background-400 mt-1">
                    {learningProgress.total_xp_earned} XP earned
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Side - Stats & Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {/* Portfolio Stats */}
              <div className="bg-background-900 border border-background-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-background-100">Portfolio Value</h3>
                    <p className="text-sm text-background-400">Virtual Trading</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-background-100 mb-1">
                  ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className={`text-sm flex items-center space-x-1 ${
                  totalGainLoss >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  <TrendingUp className="w-4 h-4" />
                  <span>
                    {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toLocaleString('en-US', { minimumFractionDigits: 2 })} 
                    ({totalGainLossPercent >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>

              {/* Learning Stats */}
              <div className="bg-background-900 border border-background-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-background-100">Learning Progress</h3>
                    <p className="text-sm text-background-400">Videos Watched</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-background-100 mb-1">
                  {learningProgress?.total_videos_watched || 0}
                </div>
                <div className="text-sm text-background-400">
                  {learningProgress?.current_streak || 0} day streak
                </div>
              </div>

              {/* Achievements */}
              <div className="bg-background-900 border border-background-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-background-100">Achievements</h3>
                    <p className="text-sm text-background-400">Earned</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-background-100 mb-1">
                  {achievements.length}
                </div>
                <div className="text-sm text-background-400">
                  {learningProgress?.modules_completed || 0} modules completed
                </div>
              </div>

              {/* Spending Stats */}
              <div className="bg-background-900 border border-background-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <Target className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-background-100">Monthly Spending</h3>
                    <p className="text-sm text-background-400">This Month</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-background-100 mb-1">
                  ${monthlySpending.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-sm text-background-400">
                  {stats.transactionCount} transactions
                </div>
              </div>
            </motion.div>

            {/* Account Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-background-900 border border-background-700 rounded-xl p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-background-700 rounded-lg">
                  <Settings className="w-5 h-5 text-background-400" />
                </div>
                <h3 className="text-lg font-semibold text-background-100">Account Settings</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-background-700">
                  <div>
                    <h4 className="font-medium text-background-100">Email Address</h4>
                    <p className="text-sm text-background-400">{user.email}</p>
                  </div>
                  <span className="text-xs text-background-500">Read-only</span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-background-700">
                  <div>
                    <h4 className="font-medium text-background-100">Account Created</h4>
                    <p className="text-sm text-background-400">
                      {new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <Calendar className="w-4 h-4 text-background-400" />
                </div>

                <div className="pt-4">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/30 rounded-lg text-red-400 hover:text-red-300 transition-all duration-200"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ProfilePage

